import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  Package,
  Gem,
  FolderOpen,
  Clock,
  AlertTriangle,
  DollarSign,
  Plus
} from 'lucide-react'
import { materialsApi } from '@/services/api'

interface DashboardStats {
  totalMaterials: number
  inventoryValue: number
  activeProjects: number
  lowStockItems: number
}

export const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchDashboardStats()
  }, [])

  const fetchDashboardStats = async () => {
    try {
      setIsLoading(true)
      console.log('📊 Fetching dashboard stats via API client...')
      
      // Use the proper API client which handles authentication and proxy
      const materialsData = await materialsApi.getAll()
      console.log('📈 Materials data received:', materialsData)
      
      // Calculate stats from materials data
      const totalMaterials = materialsData.data?.length || 0
      const inventoryValue = materialsData.data?.reduce((sum: number, material: any) => {
        return sum + (material.totalValue || 0)
      }, 0) || 0
      
      setStats({
        totalMaterials,
        inventoryValue,
        activeProjects: 0, // Will be updated when projects API is connected
        lowStockItems: 0 // Will be calculated from low stock materials
      })
      
      console.log('✅ Dashboard stats updated:', { totalMaterials, inventoryValue })
    } catch (error) {
      console.error('❌ Error fetching dashboard stats:', error)
      // Set default values on error
      setStats({
        totalMaterials: 0,
        inventoryValue: 0,
        activeProjects: 0,
        lowStockItems: 0
      })
    } finally {
      setIsLoading(false)
    }
  }
  return (
    <>
      <Helmet>
        <title>Dashboard - Jewelry Inventory Tracker</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Welcome back! Here's an overview of your jewelry business.
          </p>
        </div>

        {/* Stats Grid - No Mock Data */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Package className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Materials
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {isLoading ? '--' : stats?.totalMaterials || 0}
                </p>
                <p className="text-xs text-gray-500">
                  {isLoading ? 'Loading...' : 'Materials in inventory'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Inventory Value
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {isLoading ? '--' : `$${(stats?.inventoryValue || 0).toFixed(2)}`}
                </p>
                <p className="text-xs text-gray-500">
                  {isLoading ? 'Loading...' : 'Total inventory value'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <FolderOpen className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Active Projects
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {isLoading ? '--' : stats?.activeProjects || 0}
                </p>
                <p className="text-xs text-gray-500">
                  {isLoading ? 'Loading...' : 'Projects in progress'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Low Stock Items
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {isLoading ? '--' : stats?.lowStockItems || 0}
                </p>
                <p className="text-xs text-gray-500">
                  {isLoading ? 'Loading...' : 'Items need reordering'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions - Proper Navigation Links */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/materials/add"
              className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Gem className="w-8 h-8 text-jewelry-primary mr-3 flex-shrink-0" />
              <div className="text-left">
                <p className="font-medium text-gray-900 dark:text-white">Add Material</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Add new materials to inventory</p>
              </div>
            </Link>

            <Link
              to="/projects/new"
              className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <FolderOpen className="w-8 h-8 text-jewelry-primary mr-3 flex-shrink-0" />
              <div className="text-left">
                <p className="font-medium text-gray-900 dark:text-white">New Project</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Start a new jewelry project</p>
              </div>
            </Link>

            <Link
              to="/time-tracking"
              className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Clock className="w-8 h-8 text-jewelry-primary mr-3 flex-shrink-0" />
              <div className="text-left">
                <p className="font-medium text-gray-900 dark:text-white">Track Time</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Start time tracking session</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Recent Activity - No Mock Data */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Recent Activity
          </h2>
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 dark:text-gray-400">
              No recent activity. Connect to backend to load real data.
            </p>
          </div>
        </div>
      </div>
    </>
  )
}